/**
 * Rubriques de « Mon entreprise » (refonte v4) : ce qu'on y règle, pour qui,
 * à quelle adresse. Une seule source pour l'accueil (`/entreprise`), les routes
 * et leurs droits. Six groupes : Identité · Membres et rôles · Activité ·
 * Ventes · Connexions · Abonnement. Chaque rubrique a sa page et ses propres
 * enregistrements ; le compte personnel (e-mail, mot de passe, thème) vit dans
 * « Mon compte », jamais ici.
 */
import {
  Briefcase, BellRing, Calculator, CalendarRange, ClipboardList, FileSpreadsheet, Handshake, Landmark, KeyRound, Map, Navigation, Palette, Plug,
  Refrigerator, ScrollText, ShieldCheck, UserCog, UserLock, type LucideIcon,
} from 'lucide-react';
import type { Capability, SpecializedModule } from '@myequip/shared';
import { t } from '../../../core/i18n.js';
import { ICONS } from '../../../design/icons.js';
import { GDPR_REQUESTS_PATH } from '../../../core/notification-target.js';

export type EntrepriseGroup = 'identite' | 'membres' | 'activite' | 'ventes' | 'connexions' | 'abonnement';

export const ENTREPRISE_GROUPS: readonly EntrepriseGroup[] = [
  'identite', 'membres', 'activite', 'ventes', 'connexions', 'abonnement',
];

export interface EntrepriseSection {
  /** Segment d'adresse : `/entreprise/<id>`. */
  id: string;
  group: EntrepriseGroup;
  icon: LucideIcon;
  /** Une de ces capacités ouvre la rubrique (la route et l'API revérifient). */
  anyOf: readonly Capability[];
  module?: SpecializedModule;
  /** Rubrique gérée sur une autre page : la ligne y mène directement. */
  to?: string;
}

const SETTINGS = ['settings.manage'] as const satisfies readonly Capability[];
const USERS = ['users.manage'] as const satisfies readonly Capability[];

export const ENTREPRISE_SECTIONS: readonly EntrepriseSection[] = [
  { id: 'identite', group: 'identite', icon: Landmark, anyOf: ['settings.manage', 'company.legal_identity'] },
  { id: 'apparence', group: 'identite', icon: Palette, anyOf: SETTINGS },
  // Documents de l'entreprise (15/10/2026) : gérés ici, lus par toute l'équipe sur la même page.
  { id: 'documents', group: 'identite', icon: ICONS.documents, anyOf: SETTINGS, to: '/entreprise/documents' },
  // Demandes RGPD de ses clients (API1-12) : l'entreprise, responsable de ses données, y répond.
  { id: 'donneesPersonnelles', group: 'identite', icon: UserLock, anyOf: SETTINGS, to: GDPR_REQUESTS_PATH },
  { id: 'membres', group: 'membres', icon: UserCog, anyOf: USERS },
  { id: 'roles', group: 'membres', icon: KeyRound, anyOf: USERS },
  // Comptable de l'entreprise (25/10/2026) : place gratuite, à part des membres.
  { id: 'comptable', group: 'membres', icon: FileSpreadsheet, anyOf: USERS },
  // Journal d'audit (décision 15) : lecture seule, pour le droit qui le consulte.
  { id: 'audit', group: 'membres', icon: ScrollText, anyOf: ['audit.view_full'] },
  // Messagerie de l'équipe (25/10/2026) : active par défaut, le patron peut la couper.
  { id: 'messagerie', group: 'membres', icon: ICONS.messages, anyOf: SETTINGS },
  // Disponibilités, absences et décisions : l'espace Équipe, hors des réglages (mêmes droits que sa rubrique).
  { id: 'equipe', group: 'membres', icon: ICONS.equipe, anyOf: ['planning.view_all', 'absence.approve'], to: '/equipe' },
  // Comptes des sous-traitants (B8) : gérer les accès, ou leur confier des missions.
  {
    id: 'sousTraitants', group: 'membres', icon: Handshake,
    anyOf: ['users.manage', 'intervention.create_any', 'planning.edit_all'], to: '/entreprise/sous-traitants',
  },
  { id: 'metiers', group: 'activite', icon: Briefcase, anyOf: SETTINGS },
  { id: 'horaires', group: 'activite', icon: CalendarRange, anyOf: SETTINGS },
  { id: 'terrain', group: 'activite', icon: ShieldCheck, anyOf: SETTINGS },
  { id: 'suivi', group: 'activite', icon: Navigation, anyOf: SETTINGS },
  { id: 'temps', group: 'activite', icon: ICONS.temps, anyOf: SETTINGS },
  { id: 'pieces', group: 'activite', icon: ICONS.stock, anyOf: SETTINGS },
  // Checklists du rapport (Q23) : une page de liste et une page à étapes par modèle.
  { id: 'checklists', group: 'activite', icon: ClipboardList, anyOf: SETTINGS },
  { id: 'clients', group: 'activite', icon: Map, anyOf: SETTINGS },
  { id: 'fluides', group: 'activite', icon: ICONS.fluides, anyOf: SETTINGS, module: 'refrigerants' },
  { id: 'devis', group: 'ventes', icon: ICONS.devis, anyOf: SETTINGS },
  { id: 'baremes', group: 'ventes', icon: Calculator, anyOf: SETTINGS },
  { id: 'catalogue', group: 'ventes', icon: Refrigerator, anyOf: SETTINGS },
  { id: 'relances', group: 'ventes', icon: BellRing, anyOf: SETTINGS },
  { id: 'avis', group: 'ventes', icon: ICONS.avis, anyOf: SETTINGS },
  { id: 'integrations', group: 'connexions', icon: Plug, anyOf: SETTINGS },
  // Reprise des données d'un autre outil (décision 15 du 19/10/2026) : ses pages vivent dans `features/import`.
  { id: 'import', group: 'connexions', icon: ICONS.import, anyOf: SETTINGS, to: '/entreprise/import' },
  { id: 'abonnement', group: 'abonnement', icon: ICONS.abonnement, anyOf: SETTINGS },
];

/** Qui peut ouvrir l'accueil de « Mon entreprise » (une rubrique au moins). */
export const ENTREPRISE_ANY: readonly Capability[] = ['settings.manage', 'users.manage', 'company.legal_identity', 'audit.view_full'];

/** Les rubriques qu'un compte peut ouvrir, dans l'ordre de l'accueil. */
export function visibleSections(
  can: (capability: Capability) => boolean, modules: readonly string[],
): EntrepriseSection[] {
  return ENTREPRISE_SECTIONS.filter((section) =>
    section.anyOf.some((capability) => can(capability))
    && (!section.module || modules.includes(section.module)));
}

export function sectionPath(section: EntrepriseSection): string {
  return section.to ?? `/entreprise/${section.id}`;
}

export function sectionTitle(id: string): string {
  return t(`entreprise.sections.${id}.titre`);
}

export function sectionHint(id: string): string {
  return t(`entreprise.sections.${id}.aide`);
}

export function groupTitle(group: EntrepriseGroup): string {
  return t(`entreprise.groupes.${group}`);
}

export function findSection(id: string): EntrepriseSection {
  const section = ENTREPRISE_SECTIONS.find((item) => item.id === id);
  if (!section) throw new Error(`Rubrique inconnue : ${id}`);
  return section;
}
